'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Chip, ChipRow } from '@/components/ui/Chip';
import { cn } from '@/lib/utils';
import { translateStageName } from '@/draw/text';
import type { DrawResult, DrawStage, Player } from '@/draw/types';
import { feederSections, type Ladder } from '../libs/ladderModel';
import { BracketStageVisualization } from '@/app/[locale]/event/draw/components/BracketVisualization';
import { RoundRobinStageVisualization } from '@/app/[locale]/event/draw/components/RoundRobinVisualization';
import { buildDrawLadders } from '../libs/ladderModel';
import { buildDrawGroups, resolveGroup, sectionId, stageOf } from '../libs/drawSelection';
import { buildDrawPathIndex, hintedGroups } from '../libs/drawPaths';
import { poolNumber } from '../libs/poolView';
import { useLadderText } from '../libs/ladderText';
import { DrawHighlightContext, type DrawHighlightValue } from './DrawHighlight';
import { LadderView } from './LadderView';
import { PoolView } from './PoolView';

// A stage chip the hovered team or match leads to (it is on another tab).
const HINT = 'ring-bracket-path bg-bracket-path-tint ring-2';

function getStagePlayers(stage: DrawStage): Player[] {
  const playerMap = new Map<string, Player>();
  for (const round of stage.rounds) {
    for (const match of round.matches) {
      if (match.player1 && !match.player1.isBye) playerMap.set(match.player1.id, match.player1);
      if (match.player2 && !match.player2.isBye) playerMap.set(match.player2.id, match.player2);
    }
  }
  return Array.from(playerMap.values());
}

function translateStage(
  stage: DrawStage,
  t: (key: string, values?: Record<string, string | number>) => string,
): DrawStage {
  return {
    ...stage,
    name: translateStageName(stage.name, t),
    rounds: stage.rounds.map((round) => ({ ...round, name: translateStageName(round.name, t) })),
  };
}

interface PublicDrawViewProps {
  draw: DrawResult;
  playerHref?: (id: string) => string | undefined;
}

export function PublicDrawView({ draw, playerHref }: PublicDrawViewProps) {
  const tNav = useTranslations('Event.drawView.stageNav');
  const tContext = useTranslations('Event.drawView.contextGroup');
  const tPool = useTranslations('Event.drawView.pool');
  const tLadder = useTranslations('Event.drawView.ladder');
  const tPreview = useTranslations('AdminDraw.drawForm.drawPreview');
  const text = useLadderText();

  const model = useMemo(() => buildDrawLadders(draw), [draw]);
  const groups = useMemo(() => buildDrawGroups(model), [model]);
  // The first default sticks: a live rebuild must not move the viewer off the stage they opened on.
  const [choice, setChoice] = useState<string | null>(() => resolveGroup(null, groups));
  const groupKey = resolveGroup(choice, groups);
  const group = groups.find((g) => g.key === groupKey);
  const sections = group?.sections ?? [];
  const sectionKeys = sections.map(sectionId).join('|');

  const rootRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef(new Map<string, HTMLElement>());
  const jumping = useRef<ReturnType<typeof setTimeout> | null>(null);
  const releaseJump = useRef<((e?: Event) => void) | null>(null);
  const [spied, setSpied] = useState<string | null>(null);
  const activeSection = spied && sections.some((s) => sectionId(s) === spied) ? spied : sections[0] ? sectionId(sections[0]) : null;

  // Layer 2 follows the reader: the section whose top has passed under the sticky toolbar is current.
  useEffect(() => {
    if (sections.length < 2) return;
    let frame = 0;
    const spy = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (jumping.current) return;
        const line = (toolbarRef.current?.getBoundingClientRect().bottom ?? 0) + 24;
        let current: string | null = null;
        for (const id of sectionKeys.split('|')) {
          const el = sectionRefs.current.get(id);
          if (el && el.getBoundingClientRect().top <= line) current = id;
        }
        setSpied(current);
      });
    };
    document.addEventListener('scroll', spy, { capture: true, passive: true });
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('scroll', spy, { capture: true });
    };
  }, [sectionKeys, sections.length]);

  // Keep the current jump chip in view by scrolling only the chip row, never the page.
  useEffect(() => {
    const row = toolbarRef.current?.querySelector<HTMLElement>('[data-section-row] [role="group"]');
    const chip = row?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!row || !chip) return;
    const offset = chip.getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft;
    row.scrollLeft = offset - (row.clientWidth - chip.offsetWidth) / 2;
  }, [activeSection]);

  const endJump = () => {
    if (jumping.current) clearTimeout(jumping.current);
    jumping.current = null;
    if (releaseJump.current) document.removeEventListener('scrollend', releaseJump.current, { capture: true });
    releaseJump.current = null;
  };

  useEffect(() => endJump, []);

  const jumpTo = (id: string) => {
    const el = sectionRefs.current.get(id);
    setSpied(id);
    if (!el) return;
    el.style.scrollMarginTop = `${(toolbarRef.current?.getBoundingClientRect().height ?? 0) + 16}px`;
    // The spy waits for the page to settle, so the highlight doesn't flicker through the pools passed on
    // the way. Scrolls inside the toolbar (centring the chip row) don't count as the page settling.
    endJump();
    const release = (e?: Event) => {
      if (e?.target instanceof Node && toolbarRef.current?.contains(e.target)) return;
      endJump();
    };
    releaseJump.current = release;
    jumping.current = setTimeout(release, 2000);
    document.addEventListener('scrollend', release, { capture: true });
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    document.getElementById(`stage-${id}`)?.focus({ preventScroll: true });
  };

  const chooseGroup = (key: string) => {
    setChoice(key);
    setSpied(null);
    // A hovered card that unmounts gets no pointerleave; don't carry its fade into the new stage.
    setHoverTeam(null);
    setHoverMatch(null);
    // Switching stage from deep in a long page starts the new stage at its top.
    const top = rootRef.current?.getBoundingClientRect().top ?? 0;
    if (top < 0) rootRef.current?.scrollIntoView({ block: 'start' });
  };

  // Path tracing: hover a team or a match, or follow a "Loser → …" link to another stage.
  const paths = useMemo(() => buildDrawPathIndex(draw.matchProgression, groups), [draw.matchProgression, groups]);
  const [hoverTeam, setHoverTeam] = useState<string | null>(null);
  const [hoverMatch, setHoverMatch] = useState<string | null>(null);
  const [focus, setFocus] = useState<DrawHighlightValue['focus']>(null);
  // Not memoised: every card below re-renders with the hover anyway (nothing under here is memo'd).
  const jump = (matchId: string) => {
    const target = paths.groupOfMatch.get(matchId);
    if (target && target !== groupKey) {
      setChoice(target);
      setSpied(null);
    }
    setHoverTeam(null);
    setHoverMatch(null);
    setFocus((f) => ({ matchId, n: (f?.n ?? 0) + 1 }));
  };
  const destinations = hoverMatch ? paths.destinations.get(hoverMatch) : undefined;
  const highlight: DrawHighlightValue = {
    team: hoverTeam,
    match: hoverMatch,
    winnerTarget: destinations?.winner ?? null,
    loserTarget: destinations?.loser ?? null,
    focus,
    hoverTeam: setHoverTeam,
    hoverMatch: setHoverMatch,
    jump,
  };
  const hinted = useMemo(
    () => hintedGroups(paths, groupKey, { team: hoverTeam, match: hoverMatch }),
    [paths, groupKey, hoverTeam, hoverMatch],
  );

  // Land a jump once the target stage (and, on phones, its round) has rendered: scroll it into
  // view, move focus to it, and flash it so the eye finds it.
  useEffect(() => {
    if (!focus) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        // Landed (or nowhere to land): clear it, so a live rebuild never re-opens the jumped-to round.
        setFocus(null);
        const el = rootRef.current?.querySelector<HTMLElement>(`[data-match-id="${CSS.escape(focus.matchId)}"]`);
        if (!el) return;
        const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView?.({ behavior: reduce ? 'auto' : 'smooth', block: 'center', inline: 'center' });
        // A legacy-layout card (BracketStageVisualization) is not focusable on its own.
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
        el.focus?.({ preventScroll: true });
        el.animate?.(
          [
            { boxShadow: '0 0 0 3px var(--bracket-path), 0 0 0 9px color-mix(in srgb, var(--bracket-path) 25%, transparent)' },
            { boxShadow: '0 0 0 3px var(--bracket-path), 0 0 0 9px color-mix(in srgb, var(--bracket-path) 25%, transparent)', offset: 0.6 },
            { boxShadow: '0 0 0 0 transparent' },
          ],
          { duration: 2000, easing: 'ease-out' },
        );
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [focus]);

  const setSectionRef = (id: string) => (el: HTMLElement | null) => {
    if (el) sectionRefs.current.set(id, el);
    else sectionRefs.current.delete(id);
  };

  if (!group) return null;

  const poolGroup = groups.find((g) => g.pools);
  const ladderGroups = groups.filter((g) => !g.pools);
  const showStageRow = groups.length > 1;
  const showSectionRow = sections.length > 1;
  const poolsLabel = poolGroup?.sections.every((s) => stageOf(s).context === 'qualifying')
    ? tContext('qualifying')
    : tPool('poolsGroup');
  const poolLabel = (p: DrawStage) => {
    const n = poolNumber(p);
    return n === null ? text.stageName(p.name) : tPool('poolChip', { number: n });
  };

  const isFutsalPool = (stage: DrawStage) =>
    draw.standings?.find((s) => s.stageId === stage.stageId)?.standings[0]?.goalsFor !== undefined;

  const renderPool = (stage: DrawStage) => {
    const standings = draw.standings?.find((s) => s.stageId === stage.stageId);
    return isFutsalPool(stage) ? (
      <RoundRobinStageVisualization
        playerHref={playerHref}
        stage={translateStage(stage, tPreview)}
        players={getStagePlayers(stage)}
        standings={standings}
        showExtraInfo={false}
        embedded
      />
    ) : (
      <PoolView
        stage={stage}
        stages={draw.stages}
        standings={standings}
        playerHref={playerHref}
        title={translateStageName(stage.name, tPreview)}
        headingId={`stage-${stage.stageId}`}
      />
    );
  };

  const renderLadder = (ladder: Ladder) => {
    const ladderEmpty =
      (ladder.rounds[0]?.matches.every((lm) => !lm.match.player1 && !lm.match.player2) ?? false) &&
      (ladder.rounds[0]?.matches.some((lm) => lm.sources.some((s) => s?.kind === 'pool')) ?? false);
    return (
      <>
        {ladderEmpty && <p className="bg-surface text-muted mb-3 rounded-md px-3 py-2 text-xs">{tLadder('slotsFillHint')}</p>}
        {ladder.layout === 'tree' ? (
          <LadderView key={ladder.stage.stageId} ladder={ladder} playerHref={playerHref} />
        ) : (
          <>
            <BracketStageVisualization
              playerHref={playerHref}
              showTitle={false}
              mobileDense
              stage={translateStage(ladder.stage, tPreview)}
              matchProgression={draw.matchProgression}
              showExtraInfo={false}
              compactMode={false}
            />
            {renderFeeders(ladder)}
          </>
        )}
      </>
    );
  };

  // A nested LadderView draws no playoffs of its own, so they are listed under it.
  const renderFeeders = (ladder: Ladder) =>
    feederSections(ladder).flatMap(({ round, feeders }) =>
      feeders.map((f) => (
        <div key={f.stage.stageId} className="mt-4 space-y-2">
          <h4 className="text-sm font-semibold">
            {text.stageName(f.stage.name)}
            <span className="text-muted font-normal"> · {tLadder('fedBy', { round: text.round(round.abbrev) })}</span>
          </h4>
          <LadderView ladder={f} nested playerHref={playerHref} />
        </div>
      )),
    );

  // A pool played as a knockout: compact (every round shown, no tabs) when tree-shaped.
  const renderPoolLadder = (ladder: Ladder) =>
    ladder.layout === 'tree' ? (
      <>
        <LadderView ladder={ladder} nested playerHref={playerHref} />
        {renderFeeders(ladder)}
      </>
    ) : (
      renderLadder(ladder)
    );

  return (
    <DrawHighlightContext.Provider value={highlight}>
    <div ref={rootRef} className="space-y-3 md:space-y-4">
      {/* Printed draws are wide: landscape, only while this view is mounted. */}
      <style>{'@page { size: landscape; }'}</style>
      {(showStageRow || showSectionRow) && (
        // Pinned flush to the top of the content scroller: the negative top cancels its padding
        // (py-3 / md:p-4) so nothing scrolls through a gap above the bar. The extra top padding keeps
        // the chips where they were; on phones the margin stops at the header's mb-2 so the bar
        // never covers it.
        <div
          ref={toolbarRef}
          data-draw-navigation-toolbar
          className="bg-card border-border sticky -top-3 z-10 -mt-2 space-y-2 border-b pt-4 pb-2 md:-top-4 md:-mt-4 md:pt-6 print:static print:hidden"
        >
          {showStageRow && (
            // ChipRow's -my-1.5 replaces space-y-2's 8px gap to the pool row; that row's -6px top
            // collapses with this margin, so +2px (8px less the row's 6px padding) restores 8px.
            <ChipRow
              label={tNav('stages')}
              activeKey={group.key}
              className={showSectionRow ? 'mb-0.5' : undefined}
            >
              {poolGroup && (
                <Chip
                  preserveCase
                  selected={group.pools}
                  data-path-hint={hinted.has(poolGroup.key) || undefined}
                  className={cn(hinted.has(poolGroup.key) && HINT)}
                  onClick={() => chooseGroup(poolGroup.key)}
                >
                  {poolsLabel}
                </Chip>
              )}
              {ladderGroups.map((g) => {
                const l = (g.sections[0] as { kind: 'ladder'; ladder: Ladder }).ladder;
                return (
                  <Chip
                    key={g.key}
                    preserveCase
                    selected={g.key === group.key}
                    data-path-hint={hinted.has(g.key) || undefined}
                    className={cn(hinted.has(g.key) && HINT)}
                    onClick={() => chooseGroup(g.key)}
                  >
                    {text.stageName(l.stage.name)}
                    {l.places && <span className="text-muted font-normal"> · {text.places(l.places)}</span>}
                  </Chip>
                );
              })}
            </ChipRow>
          )}
          {showSectionRow && (
            <div data-section-row>
              <ChipRow label={tNav('pools')}>
                {sections.map((s) => (
                  <Chip key={sectionId(s)} preserveCase selected={sectionId(s) === activeSection} onClick={() => jumpTo(sectionId(s))}>
                    {poolLabel(stageOf(s))}
                  </Chip>
                ))}
              </ChipRow>
            </div>
          )}
        </div>
      )}

      {group.pools ? (
        <div className="space-y-8">
          {sections.map((s) => {
            const stage = stageOf(s);
            return (
              <section
                key={stage.stageId}
                ref={setSectionRef(stage.stageId)}
                data-stage-id={stage.stageId}
                aria-labelledby={`stage-${stage.stageId}`}
                className="border-border min-w-0 space-y-3 border-t pt-6 first:border-0 first:pt-0"
              >
                {/* PoolView renders its own heading in its header strip. */}
                {!(s.kind === 'pool' && !isFutsalPool(s.stage)) && (
                  <h3 id={`stage-${stage.stageId}`} tabIndex={-1} className="text-base font-semibold focus:outline-none">
                    {translateStageName(stage.name, tPreview)}
                  </h3>
                )}
                {s.kind === 'pool' ? renderPool(s.stage) : renderPoolLadder(s.ladder)}
              </section>
            );
          })}
        </div>
      ) : (
        sections.map((s) => {
          const stage = stageOf(s);
          return (
            <div key={stage.stageId} data-stage-id={stage.stageId} className="min-w-0">
              <h3 className="sr-only">{translateStageName(stage.name, tPreview)}</h3>
              {s.kind === 'ladder' && renderLadder(s.ladder)}
            </div>
          );
        })
      )}
    </div>
    </DrawHighlightContext.Provider>
  );
}
